// Port of ios/Sources/Session/SessionEndRecord.swift.
//
// Why the last session ended, and when — so a sign-out the reader did not ask for can explain
// itself instead of just appearing as a login screen.
//
// "Out of nowhere the session got logged out" (user, 2026-09-08) is unanswerable after the fact:
// the app is back at Login with no memory of what happened. One breadcrumb, written at the moment of
// the sign-out, is the difference between the next report being a mystery and being a fact.
//
// WEB: kept in `localStorage` (the caller passes the storage) — it must survive the sign-out that
// wipes the database, holds no token, and is safe to show on a screen anybody can see.

import { tf } from '../i18n/i18n';

export type SessionEndReason = 'rejectedByServer' | 'userRequested';

/** The slice of `Storage` this needs (tests pass a Map-backed one). */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const reasonKey = 'satori.session_end_reason';
const dateKey = 'satori.session_end_at';

function record(storage: KeyValueStore, reason: SessionEndReason, atMillis: number): void {
  storage.setItem(reasonKey, reason);
  storage.setItem(dateKey, String(atMillis));
}

/**
 * Cleared on a successful sign-in: the note explains the login screen you are looking at, and once
 * you are past it there is nothing left to explain.
 */
function clear(storage: KeyValueStore): void {
  storage.removeItem(reasonKey);
  storage.removeItem(dateKey);
}

function last(storage: KeyValueStore): { reason: SessionEndReason; at: number } | null {
  const raw = storage.getItem(reasonKey);
  if (raw !== 'rejectedByServer' && raw !== 'userRequested') return null;
  const at = Number(storage.getItem(dateKey) ?? 0);
  if (!(at > 0)) return null;
  return { reason: raw, at };
}

/**
 * The sentence for the login screen. Null for a sign-out the reader performed themselves — they
 * know why they are there. `formatTime` draws the moment in the reader's clock (short time).
 */
function explanation(storage: KeyValueStore, formatTime: (atMillis: number) => string): string | null {
  const l = last(storage);
  if (!l || l.reason !== 'rejectedByServer') return null;
  return tf('login.notice.sessionRejected', formatTime(l.at));
}

export const SessionEndRecord = { record, clear, last, explanation } as const;
